import React, {
    useCallback,
    useContext,
    useEffect,
    useLayoutEffect,
    useRef,
    useState,
} from "react";
import styles from "./DatePicker.module.css";

import datePickerIcon from "../icons/date-picker.svg";
import Separator from "../Separator";
import Select from "../select";
import BackButton from "../back-button/BackButton";
import useOnClickOutside from "../hooks/useOnClickOutside";
import useOnScrollOutside from "../hooks/useOnScrollOutside";
import useOnResize from "../hooks/useOnResize";
import { MONTH_LONG } from "../utils/dateUtils";
import { UserDetailsContext } from "App";

/* ------------------------------------------------------------------ *
 * TIMEZONE BOUNDARY HELPERS
 *
 * The component's internal calendar math never touches the browser's
 * local system timezone. Internally, a selected day is represented as
 * a plain { year, month, day } triple, and grid math is done using a
 * "UTC anchor" Date (Date.UTC(...) + getUTC*\/setUTC* only) purely as
 a calendar-arithmetic convenience - it carries no real timezone
 * meaning of its own.
 *
 * The user's configured timezone is applied at exactly two boundaries:
 *   1. INPUT  - converting an incoming UTC instant (or "today") into
 *               calendar parts, interpreted in the user's timezone.
 *   2. OUTPUT - converting the selected calendar parts into a real UTC
 *               instant representing midnight in the user's timezone,
 *               which is what gets sent to onChange.
 * ------------------------------------------------------------------ */

export const toValidDate = (value) => {
  if (!value || value === "Invalid date") return null;

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  if (typeof value?.toDate === "function") {
    const date = value.toDate();
    return Number.isNaN(date.getTime()) ? null : date;
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

// Read {year, month (1-12), day} for a given instant, as seen in `timezone`.
const getDatePartsInTimezone = (instant, timezone) => {
    if (!instant) return null;

    const parts = new Intl.DateTimeFormat("en-US", {
        timeZone: timezone || "UTC",
        year: "numeric",
        month: "numeric",
        day: "numeric",
    }).formatToParts(instant);

    return Object.fromEntries(
        parts
            .filter(({ type }) => type !== "literal")
            .map(({ type, value }) => [type, Number(value)]),
    );
};

// Convert calendar parts (midnight, "wall clock" in `timezone`) into a
// real UTC Date instant. DST-safe: derives the correct offset for that
// specific calendar date rather than assuming a fixed offset.
const zonedDatePartsToUTCInstant = (year, month, day, timezone) => {
    // month here is 0-indexed to match JS Date conventions internally
    const guess = new Date(Date.UTC(year, month, day, 0, 0, 0));

    const dtf = new Intl.DateTimeFormat("en-US", {
        timeZone: timezone || "UTC",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hourCycle: "h23",
    });

    const parts = Object.fromEntries(
        dtf
            .formatToParts(guess)
            .filter((p) => p.type !== "literal")
            .map((p) => [p.type, p.value]),
    );

    const guessReadBackAsUTC = Date.UTC(
        Number(parts.year),
        Number(parts.month) - 1,
        Number(parts.day),
        Number(parts.hour),
        Number(parts.minute),
        Number(parts.second),
    );

    // Correction = how far off the naive UTC guess was from the actual
    // offset that applies to this calendar date in `timezone`.
    const correction = guess.getTime() - guessReadBackAsUTC;
    return new Date(guess.getTime() + correction);
};

// Parse an incoming value (ISO date string, ISO instant string, epoch ms,
// or Date) into a real instant we can re-project through the user's tz.
const MONTH_ABBR = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

// Mirrors the backend's approach: strip the time portion off a combined
// pattern by cutting at the first space, leaving just the date part.
const extractDatePattern = (dateTimeFormat) => {
    if (!dateTimeFormat) return "yyyy-MM-dd";
    const spaceIdx = dateTimeFormat.indexOf(" ");
    return spaceIdx > 0 ? dateTimeFormat.slice(0, spaceIdx) : dateTimeFormat;
};

// Turn a pattern like "dd.MM.yyyy" or "MMM-dd-yyyy" into a regex plus
// the order its capture groups appear in - parsing driven purely by
// the pattern string, no per-format special-casing needed.
const buildRegexFromPattern = (pattern) => {
    const tokenRegex = /yyyy|MMM|MM|dd/g;
    let regexStr = "^";
    const order = [];
    let lastIndex = 0;
    let match;

    while ((match = tokenRegex.exec(pattern))) {
        if (match.index > lastIndex) {
            regexStr += pattern.slice(lastIndex, match.index).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
        }
        switch (match[0]) {
            case "yyyy": regexStr += "(\\d{4})"; order.push("yyyy"); break;
            case "MM":   regexStr += "(\\d{1,2})"; order.push("MM"); break;
            case "dd":   regexStr += "(\\d{1,2})"; order.push("dd"); break;
            case "MMM":  regexStr += "([A-Za-z]{3})"; order.push("MMM"); break;
        }
        lastIndex = tokenRegex.lastIndex;
    }
    if (lastIndex < pattern.length) {
        regexStr += pattern.slice(lastIndex).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    }

    return { regex: new RegExp(regexStr + "$"), order };
};

const parseCalendarDateParts = (value, datePattern) => {
    const { regex, order } = buildRegexFromPattern(datePattern);
    const match = regex.exec(value.trim());
    if (!match) return null;

    let year, month, day;
    for (let i = 0; i < order.length; i++) {
        const raw = match[i + 1];
        if (order[i] === "yyyy") year = Number(raw);
        else if (order[i] === "MM") month = Number(raw);
        else if (order[i] === "dd") day = Number(raw);
        else if (order[i] === "MMM") {
            const idx = MONTH_ABBR.findIndex((m) => m.toLowerCase() === raw.toLowerCase());
            if (idx === -1) return null;
            month = idx + 1;
        }
    }

    return (year && month && day) ? { year, month, day } : null;
};

// `dateTimeFormat` is userDetails.dateTimeFormat, e.g. "dd.MM.yyyy hh:mm:ss" -
// only the date portion is used; the time portion (if present) is ignored,
// since this always parses a plain calendar date with no time component.
const toInstant = (value, dateTimeFormat) => {
    if (!value) return null;
    if (value instanceof Date) return value;
    if (typeof value === "number") return new Date(value);

    if (typeof value === "string") {
        // Fast path - already plain ISO calendar date
        if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
            return new Date(`${value}T00:00:00Z`);
        }
        // Already a full ISO date-time / instant string
        if (/^\d{4}-\d{2}-\d{2}T/.test(value)) {
            const parsed = new Date(value);
            return isNaN(parsed.getTime()) ? null : parsed;
        }

        // User's configured format, e.g. "dd.MM.yyyy" for "14.08.2026"
        const datePattern = extractDatePattern(dateTimeFormat);
        const parts = parseCalendarDateParts(value, datePattern);
        if (parts) {
            return new Date(Date.UTC(parts.year, parts.month - 1, parts.day, 0, 0, 0));
        }

        return null; // don't fall back to `new Date(string)` - that's exactly
                     // the ambiguous/inconsistent parsing this exists to avoid
    }

    return null;
};

/* ------------------------------------------------------------------ *
 * TIMEZONE-BLIND CALENDAR ARITHMETIC (UTC-anchor based)
 * These never call local (non-UTC) Date getters/setters, so results
 * never depend on the browser's system timezone.
 * ------------------------------------------------------------------ */

const utcAnchor = (year, month, day) => new Date(Date.UTC(year, month, day));

const partsOf = (anchor) => ({
    year: anchor.getUTCFullYear(),
    month: anchor.getUTCMonth(),
    day: anchor.getUTCDate(),
});

const daysInMonthUTC = (anchor) =>
    new Date(Date.UTC(anchor.getUTCFullYear(), anchor.getUTCMonth() + 1, 0)).getUTCDate();

const isSameDayUTC = (a, b) => {
    if (!a || !b) return false;
    return (
        a.getUTCFullYear() === b.getUTCFullYear() &&
        a.getUTCMonth() === b.getUTCMonth() &&
        a.getUTCDate() === b.getUTCDate()
    );
};

const addDaysUTC = (anchor, days) => {
    const next = new Date(anchor);
    next.setUTCDate(next.getUTCDate() + days);
    return next;
};

const addMonthsUTC = (anchor, months) => {
    const originalDay = anchor.getUTCDate();
    const next = new Date(anchor);
    next.setUTCDate(1);
    next.setUTCMonth(next.getUTCMonth() + months);
    next.setUTCDate(Math.min(originalDay, daysInMonthUTC(next)));
    return next;
};

const isBeforeDayUTC = (anchor, minAnchor) =>
    minAnchor ? anchor.getTime() < minAnchor.getTime() : false;

const isAfterDayUTC = (anchor, maxAnchor) =>
    maxAnchor ? anchor.getTime() > maxAnchor.getTime() : false;

const isDateDisabledUTC = (anchor, minAnchor, maxAnchor) =>
    isBeforeDayUTC(anchor, minAnchor) || isAfterDayUTC(anchor, maxAnchor);

const getLaterAnchor = (a, b) => {
    if (!a) return b;
    if (!b) return a;
    return a.getTime() > b.getTime() ? a : b;
};

const getEarlierAnchor = (a, b) => {
    if (!a) return b;
    if (!b) return a;
    return a.getTime() < b.getTime() ? a : b;
};

const getConstrainedAnchor = (anchor, minAnchor, maxAnchor) => {
    if (!anchor) return null;
    if (isBeforeDayUTC(anchor, minAnchor)) return minAnchor;
    if (isAfterDayUTC(anchor, maxAnchor)) return maxAnchor;
    return anchor;
};

// Convert an incoming prop value (instant/string/Date) into a UTC-anchor
// calendar date, interpreted in the user's timezone.
const valueToAnchor = (value, timezone, dateTimeFormat) => {
    const instant = toInstant(value, dateTimeFormat);
    if (!instant) return null;
    const parts = getDatePartsInTimezone(instant, timezone);
    return utcAnchor(parts.year, parts.month - 1, parts.day);
};

const todayAnchor = (timezone) => {
    const parts = getDatePartsInTimezone(new Date(), timezone);
    return utcAnchor(parts.year, parts.month - 1, parts.day);
};

const formatAnchorDisplay = (anchor) => {
    if (!anchor) return "Select the Date";
    const { year, month, day } = partsOf(anchor);
    return `${String(day).padStart(2, "0")}-${String(month + 1).padStart(2, "0")}-${year}`;
};

const DateOnlyPicker = ({
                            value,
                            onChange,
                            minDate,
                            maxDate,
                            disablePast = false,
                            disableFuture = false,
                            yearStart = 1900,
                            yearEnd = new Date().getFullYear() + 100,
                            ariaLabel = "Choose date",
                        }) => {
    const userDetails = useContext(UserDetailsContext);
    const timeZone = userDetails.timeZone || "UTC";
    const dateTimeFormat = userDetails.dateTimeFormat || "mm/dd/yyyy";

    const [open, setOpen] = useState(false);
    const [dropdownStyle, setDropdownStyle] = useState({});
    const [isPositioned, setIsPositioned] = useState(false);

    // Selected date lives as a UTC-anchor calendar date, resolved from
    // `value` using the user's timezone - never the browser's local tz.
    const [selectedAnchor, setSelectedAnchor] = useState(() =>
        valueToAnchor(value, timeZone, dateTimeFormat),
    );

    const today = todayAnchor(timeZone);

    const minAnchor = valueToAnchor(minDate, timeZone, dateTimeFormat);
    const maxAnchor = valueToAnchor(maxDate, timeZone, dateTimeFormat);

    const effectiveMinAnchor = getLaterAnchor(minAnchor, disablePast ? today : null);
    const effectiveMaxAnchor = getEarlierAnchor(maxAnchor, disableFuture ? today : null);

    useEffect(() => {
        const nextAnchor = valueToAnchor(value, timeZone, dateTimeFormat);
        if (nextAnchor) {
            if (!selectedAnchor || !isSameDayUTC(selectedAnchor, nextAnchor)) {
                setSelectedAnchor(nextAnchor);
            }
        } else if (selectedAnchor) {
            setSelectedAnchor(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value, timeZone]);

    const datePickerRef = useRef(null);
    const contentWrapperRef = useRef(null);

    const toggle = () => {
        setOpen((prevOpen) => {
            const nextOpen = !prevOpen;
            if (nextOpen) {
                setIsPositioned(false);
                setDropdownStyle({ top: "-9999px", left: "-9999px" });
            }
            return nextOpen;
        });
    };

    const updateDropdownPosition = useCallback(() => {
        if (!datePickerRef.current || !contentWrapperRef.current) return;

        const trigger = datePickerRef.current.getBoundingClientRect();
        const content = contentWrapperRef.current.getBoundingClientRect();

        const spaceBelow = window.innerHeight - trigger.bottom;
        const spaceAbove = trigger.top;
        const openUpwards = spaceBelow < content.height && spaceAbove >= spaceBelow;

        const scrollY = window.scrollY || window.pageYOffset || 0;
        const scrollX = window.scrollX || window.pageXOffset || 0;

        const top = openUpwards
            ? trigger.top + scrollY - content.height
            : trigger.bottom + scrollY;

        setDropdownStyle({
            top: `${Math.max(8, top)}px`,
            left: `${trigger.left + scrollX}px`,
        });
        setIsPositioned(true);
    }, []);

    const handleClose = useCallback(() => setOpen(false), []);

    const handleDateSelect = useCallback(
        (selectedAnchorDate) => {
            setSelectedAnchor(selectedAnchorDate);

            if (onChange) {
                onChange(selectedAnchorDate);
            }

            handleClose();
        },
        [onChange, handleClose, timeZone],
    );

    useOnClickOutside(datePickerRef, handleClose);
    useOnScrollOutside(datePickerRef, handleClose);
    useOnResize(handleClose);

    useLayoutEffect(() => {
        if (!open) return;
        updateDropdownPosition();
    }, [open, updateDropdownPosition]);

    return (
        <div
            className={[styles.DatePicker, open && styles.datePickerRootOpen].join(" ")}
            ref={datePickerRef}
        >
            <DatePickerTrigger anchor={selectedAnchor} open={open} toggle={toggle} />
            <div
                className={[styles.contentWrapper, open && styles.show].join(" ")}
                role="dialog"
                aria-label={ariaLabel}
                style={{
                    ...dropdownStyle,
                    visibility: open && !isPositioned ? "hidden" : "visible",
                    pointerEvents: open && !isPositioned ? "none" : "auto",
                }}
                ref={contentWrapperRef}
            >
                {open && (
                    <DatePickerContent
                        selectedAnchor={selectedAnchor}
                        today={today}
                        onSelectDay={handleDateSelect}
                        onClose={handleClose}
                        minAnchor={effectiveMinAnchor}
                        maxAnchor={effectiveMaxAnchor}
                        yearStart={yearStart}
                        yearEnd={yearEnd}
                    />
                )}
            </div>
        </div>
    );
};

const DatePickerTrigger = ({ anchor, open, toggle }) => (
    <div
        onClick={toggle}
        onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                toggle();
            }
        }}
        role="button"
        tabIndex={0}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label="Open date picker"
        className={[styles.datePicker, open && styles.datePickerOpen].join(" ")}
    >
        <span>{formatAnchorDisplay(anchor)}</span>
        <img src={datePickerIcon} alt="date picker" width={20} height={20} />
    </div>
);

const DatePickerContent = ({
                               selectedAnchor,
                               today,
                               onSelectDay,
                               onClose,
                               minAnchor,
                               maxAnchor,
                               yearStart,
                               yearEnd,
                           }) => {
    const [displayAnchor, setDisplayAnchor] = useState(selectedAnchor || today);
    const [focusedAnchor, setFocusedAnchor] = useState(selectedAnchor || today);
    const calendarRef = useRef(null);

    useEffect(() => {
        const base = selectedAnchor || today;
        setDisplayAnchor(base);
        setFocusedAnchor(base);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedAnchor]);

    useEffect(() => {
        calendarRef.current?.focus();
    }, []);

    const { year: displayYear, month: displayMonth } = partsOf(displayAnchor);
    const monthStartsAt = new Date(Date.UTC(displayYear, displayMonth, 1)).getUTCDay();
    const daysInCurrentMonth = daysInMonthUTC(displayAnchor);

    const getAnchorForDay = (day) => utcAnchor(displayYear, displayMonth, day);

    const selectDate = (anchor) => {
        if (isDateDisabledUTC(anchor, minAnchor, maxAnchor)) return;
        onSelectDay(anchor);
    };

    const setDay = (day) => selectDate(getAnchorForDay(day));

    const moveFocus = (nextAnchor) => {
        const constrained = getConstrainedAnchor(nextAnchor, minAnchor, maxAnchor) || nextAnchor;
        setFocusedAnchor(constrained);
        setDisplayAnchor(constrained);
    };

    const handleKeyDown = (event) => {
        const keyHandlers = {
            ArrowLeft: () => moveFocus(addDaysUTC(focusedAnchor, -1)),
            ArrowRight: () => moveFocus(addDaysUTC(focusedAnchor, 1)),
            ArrowUp: () => moveFocus(addDaysUTC(focusedAnchor, -7)),
            ArrowDown: () => moveFocus(addDaysUTC(focusedAnchor, 7)),
            PageUp: () => moveFocus(addMonthsUTC(focusedAnchor, -1)),
            PageDown: () => moveFocus(addMonthsUTC(focusedAnchor, 1)),
            Home: () => moveFocus(addDaysUTC(focusedAnchor, -focusedAnchor.getUTCDay())),
            End: () => moveFocus(addDaysUTC(focusedAnchor, 6 - focusedAnchor.getUTCDay())),
            Enter: () => selectDate(focusedAnchor),
            Escape: onClose,
        };

        if (!keyHandlers[event.key]) return;
        event.preventDefault();
        keyHandlers[event.key]();
    };

    const lastDayOfWeek = new Date(Date.UTC(displayYear, displayMonth + 1, 0)).getUTCDay();
    const endPadding = (6 - lastDayOfWeek + 7) % 7;

    return (
        <div className={styles.DatePickerContent}>
            <div className={styles.contentChildren}>
                <MonthAndYear
                    anchor={displayAnchor}
                    setAnchor={(nextAnchor) => {
                        setDisplayAnchor(nextAnchor);
                        setFocusedAnchor(nextAnchor);
                    }}
                    minAnchor={minAnchor}
                    maxAnchor={maxAnchor}
                    yearStart={yearStart}
                    yearEnd={yearEnd}
                />
                <Separator />
                <div
                    className={styles.dates}
                    role="grid"
                    aria-label={`${MONTH_LONG[displayMonth]} ${displayYear}`}
                    tabIndex={0}
                    onKeyDown={handleKeyDown}
                    ref={calendarRef}
                >
                    {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
                        <span key={day} role="columnheader">
              {day}
            </span>
                    ))}
                    {Array.from({ length: monthStartsAt }).map((_, ind) => (
                        <span key={`start-pad-${ind}`} className={styles.unSelectableDate}></span>
                    ))}
                    {Array.from({ length: daysInCurrentMonth }).map((_, index) => {
                        const dayNumber = index + 1;
                        const dayAnchor = getAnchorForDay(dayNumber);
                        const isSelected = selectedAnchor && isSameDayUTC(selectedAnchor, dayAnchor);
                        const isFocused = isSameDayUTC(focusedAnchor, dayAnchor);
                        const isDisabled = isDateDisabledUTC(dayAnchor, minAnchor, maxAnchor);
                        return (
                            <button
                                type="button"
                                key={dayNumber}
                                className={[
                                    styles.date,
                                    isSelected && styles.active,
                                    isFocused && styles.focused,
                                    isDisabled && styles.disabledDate,
                                ].join(" ")}
                                onClick={() => setDay(dayNumber)}
                                disabled={isDisabled}
                                role="gridcell"
                                aria-selected={isSelected}
                                aria-label={formatAnchorDisplay(dayAnchor)}
                            >
                                {dayNumber}
                            </button>
                        );
                    })}
                    {Array.from({ length: endPadding }).map((_, ind) => (
                        <span key={`end-pad-${ind}`} className={styles.unSelectableDate}></span>
                    ))}
                </div>
                <Separator />
            </div>
        </div>
    );
};

const MonthAndYear = ({ anchor, setAnchor, minAnchor, maxAnchor, yearStart, yearEnd }) => {
    const { year: currentYear, month: currentMonth } = partsOf(anchor);

    const startYear = minAnchor ? minAnchor.getUTCFullYear() : yearStart;
    const endYear = Math.max(startYear, maxAnchor ? maxAnchor.getUTCFullYear() : yearEnd);
    const totalYears = endYear - startYear + 1;

    const updateAnchor = (modifier) => {
        const next = modifier(anchor);
        setAnchor(getConstrainedAnchor(next, minAnchor, maxAnchor) || next);
    };

    const nextMonth = () => updateAnchor((a) => addMonthsUTC(a, 1));
    const prevMonth = () => updateAnchor((a) => addMonthsUTC(a, -1));

    return (
        <div className={styles.monthAndYear}>
            <BackButton type="button" onClick={prevMonth} />
            <div style={{ display: "flex", gap: "0.6rem" }}>
                <Select
                    options={MONTH_LONG}
                    active={currentMonth}
                    triggerProps={{ className: styles.select }}
                    childrenProps={{
                        style: {
                            display: "grid",
                            gridTemplateColumns: "repeat(3, 1fr)",
                            placeItems: "center",
                        },
                    }}
                    onOptionSelect={(_month, index) =>
                        updateAnchor((a) => utcAnchor(a.getUTCFullYear(), index, Math.min(a.getUTCDate(), daysInMonthUTC(utcAnchor(a.getUTCFullYear(), index, 1)))))
                    }
                />
                <Select
                    options={Array.from({ length: totalYears }, (_, i) => i + startYear)}
                    active={currentYear - startYear}
                    triggerProps={{ className: styles.select }}
                    childrenProps={{
                        style: {
                            display: "grid",
                            gridTemplateColumns: "repeat(3, 1fr)",
                            placeItems: "center",
                            maxHeight: "14rem",
                            overflowY: "auto",
                        },
                    }}
                    onOptionSelect={(_year, index) =>
                        updateAnchor((a) => utcAnchor(index + startYear, a.getUTCMonth(), a.getUTCDate()))
                    }
                />
            </div>
            <BackButton style={{ transform: "rotate(180deg)" }} onClick={nextMonth} type="button" />
        </div>
    );
};

export default DateOnlyPicker;